import React from 'react';
import { InboxArrowDownIcon, PaperAirplaneIcon } from '@heroicons/react/20/solid';
import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@oysterjs/ui/dialog';
import { messageFromError } from '@oysterjs/core/graphql/errors';

import { sendEmailBulkSend, sendRestOfEmailBulkSend } from '~/api/data/email-bulk-send/mutations';
import type { MovedToDrafts } from '~/components/mailbox/moved-to-drafts-toast';
import { ScheduleSendDialog } from '~/components/schedule-send-dialog';
import { ScheduleSendMenu } from '~/components/workspaces/artifact-focus-email-schedule-menu';
import {
  EmailBulkSendAction,
  type MailboxAccountFieldsFragment,
  type MailboxBulkSendUsageQuery,
  useMailboxBulkSendUsageQuery
} from '~/api/graphql';
import { type ScheduleQuickPick, tomorrowMorningOf } from '~/lib/scheduled-send';
import { InlineNotice } from '~/components/inline-notice';
import { idempotencyKeys } from '~/lib/idempotency';
import { draftsLocationName, mailClientName } from '~/lib/mailbox-providers';
import { pluralize } from '~/lib/pluralize';
import { NEEDS_RECONNECT_REASON } from './bulk-draft-send-from';

/** A lost answer may hide a batch that did start, so the agent looks before trying again. */
const LOST_ANSWER = 'Check Outgoing before trying again.';
const REFUSED_WITHOUT_REASON = "Cara couldn't start it.";
const SENDING_IS_FINAL = "Sent emails can't be undone.";

export const NOTHING_COMPOSED = 'No composed emails to send.';

/**
 * Why an action that takes `emailCount` emails can't run yet, most pressing first; null when
 * it can.
 */
export const bulkDraftActionsOffReason = (opts: {
  isDrafting: boolean;
  hasMailbox: boolean;
  sendingMailbox: MailboxAccountFieldsFragment | undefined;
  emailCount: number;
}): string | null => {
  if (opts.isDrafting) return 'Wait for Cara to finish drafting.';
  if (!opts.hasMailbox) return 'Connect a mailbox to send.';
  if (!opts.sendingMailbox) return NEEDS_RECONNECT_REASON;
  if (opts.emailCount === 0) return NOTHING_COMPOSED;
  return null;
};

const DAILY_BULK_LIMIT_REACHED = "This mailbox has reached today's bulk limit.";
const DAILY_BULK_LIMIT_REASON = `${DAILY_BULK_LIMIT_REACHED} Use the arrow beside Send all to schedule the rest.`;

export type MailboxBulkSendUsage = MailboxBulkSendUsageQuery['mailboxBulkSendUsage'];

/**
 * The mailbox's bulk emails in the last day, and the earliest time from tomorrow morning that
 * readyCount more fit. Undefined with no mailbox, while its first read loads, or when that fails.
 */
export const useMailboxBulkSendUsage = (
  mailboxAccountId: string | undefined,
  readyCount: number
): MailboxBulkSendUsage | undefined => {
  // A new readyCount reads again, and the query has no data until it answers. The mailbox's last
  // answer stands meanwhile, so the limit line and Send all don't blink as drafting finishes.
  const [lastAnswer, setLastAnswer] = React.useState<{
    mailboxAccountId?: string;
    usage?: MailboxBulkSendUsage;
  }>({});
  const { data } = useMailboxBulkSendUsageQuery({
    variables: {
      mailboxAccountId: mailboxAccountId ?? '',
      // The server refuses a count of 0; with nothing ready the earliest time goes unused.
      count: Math.max(readyCount, 1),
      notBefore: tomorrowMorningOf(new Date())
    },
    skip: !mailboxAccountId
  });
  const usage = data?.mailboxBulkSendUsage;
  if (usage && usage !== lastAnswer.usage) {
    setLastAnswer({ mailboxAccountId, usage });
  }
  const isLastAnswerForMailbox = lastAnswer.mailboxAccountId === mailboxAccountId;
  return usage ?? (isLastAnswerForMailbox ? lastAnswer.usage : undefined);
};

/** Whether sending this batch now would take the mailbox past its daily bulk limit. */
export const wouldExceedDailyBulkLimit = (
  usage: MailboxBulkSendUsage | undefined,
  readyCount: number
) => !!usage && usage.usedInDailyWindow + readyCount > usage.dailyLimit;

/** Where Schedule all's dialog opens: on a quick pick, or on a time of its own. */
type ScheduleStart = { quickPick?: ScheduleQuickPick['label']; sendAt?: Date };

/** What Send answered with: when it started, and where Move all to drafts saved the emails. */
export type StartedBulkSend = { sentAt: Date | null | undefined; movedToDrafts?: MovedToDrafts };

const heldBackLine = (heldBackCount: number) =>
  `The ${heldBackCount} ${pluralize(heldBackCount, 'email')} with issues ${
    heldBackCount === 1 ? 'is' : 'are'
  } left out.`;

/**
 * Send all, with the other ways a bulk draft becomes a batch in its arrow menu, each behind its
 * confirm. Each takes the `readyCount` emails. As Send the rest, it sends or schedules a sent
 * batch's emails that never went out.
 */
export const BulkDraftActions = ({
  scope = 'all',
  bulkSendId,
  sendingMailbox,
  recipientCount,
  readyCount,
  offReason,
  usage,
  onStarted
}: {
  scope?: 'all' | 'rest';
  bulkSendId: string;
  sendingMailbox: MailboxAccountFieldsFragment | undefined;
  recipientCount: number;
  readyCount: number;
  offReason: string | null;
  /** A failed read leaves Send all on; the server refuses a send past the limit anyway. */
  usage: MailboxBulkSendUsage | undefined;
  /** Shows the page sent. The confirm stays on Starting… until it resolves. */
  onStarted: (started: StartedBulkSend) => Promise<void>;
}) => {
  // The same request again reuses its key, so a retry returns the batch the first one
  // started; another action, mailbox or time is a new attempt.
  const attemptIds = React.useRef(idempotencyKeys());
  const [confirming, setConfirming] = React.useState<EmailBulkSendAction>();
  const [scheduleStart, setScheduleStart] = React.useState<ScheduleStart>({});
  const [isStarting, setIsStarting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const tomorrowMorning = tomorrowMorningOf(new Date());
  const isPastDailyLimit = wouldExceedDailyBulkLimit(usage, readyCount);
  // Null when the batch alone is past the limit; Schedule then opens on tomorrow morning and
  // the server says why it can't start.
  const earliestSendAt = usage?.earliestSendAt ? new Date(usage.earliestSendAt) : null;
  const tomorrowStart: ScheduleStart =
    earliestSendAt && earliestSendAt > tomorrowMorning
      ? { sendAt: earliestSendAt }
      : { quickPick: 'Tomorrow morning' };
  // Past today's limit, a time picked by hand starts on the first time the batch fits; a quick
  // pick keeps the time its label shows.
  const scheduleStartFor = (quickPick?: ScheduleQuickPick['label']): ScheduleStart =>
    isPastDailyLimit && !quickPick ? tomorrowStart : { quickPick };
  const sendAllOffReason = offReason ?? (isPastDailyLimit ? DAILY_BULK_LIMIT_REASON : null);
  const heldBackCount = recipientCount - readyCount;
  const emails = `${readyCount} ${pluralize(readyCount, 'email')}`;
  const isRest = scope === 'rest';
  const sendLabel = isRest ? 'Send the rest' : 'Send all';
  const fromAddress = sendingMailbox?.identity.email ?? '';
  const sendingFrom = `Sending from ${fromAddress}.`;

  const start = async (action: EmailBulkSendAction, sendAt?: Date) => {
    if (!sendingMailbox) return;
    setIsStarting(true);
    setError(null);
    try {
      const request = { mailboxAccountId: sendingMailbox.id, action, sendAt };
      const attemptId = attemptIds.current.keyFor(request);
      const { batch, error: refusal } = isRest
        ? await sendRestOfEmailBulkSend({
            id: bulkSendId,
            input: { attemptId, action, sendAt }
          }).then((result) => ({
            batch: result.data?.sendRestOfEmailBulkSend,
            error: result.error
          }))
        : await sendEmailBulkSend({
            id: bulkSendId,
            input: { ...request, attemptId }
          }).then((result) => ({ batch: result.data?.sendEmailBulkSend, error: result.error }));
      if (refusal) {
        // A refused Send leaves nothing a retry should return, so trying again is a new attempt.
        attemptIds.current.reset();
        setError(`${messageFromError(refusal, REFUSED_WITHOUT_REASON)} Nothing was sent.`);
        return;
      }
      if (!batch) {
        setError(LOST_ANSWER);
        return;
      }
      // Named from the batch the server returned, which a retried attempt may have started
      // with another action.
      const isMovedToDrafts = batch.action === EmailBulkSendAction.MoveToDrafts;
      await onStarted({
        sentAt: batch.sentAt,
        movedToDrafts: isMovedToDrafts
          ? {
              count: readyCount,
              fromAddress: batch.fromAddress ?? fromAddress,
              provider: sendingMailbox.provider
            }
          : undefined
      });
      attemptIds.current.reset();
      close();
    } catch (startError) {
      setError(messageFromError(startError, LOST_ANSWER));
    } finally {
      setIsStarting(false);
    }
  };

  const close = () => {
    setConfirming(undefined);
    setScheduleStart({});
    setError(null);
  };

  const openSchedule = (quickPick?: ScheduleQuickPick['label']) => {
    setScheduleStart(scheduleStartFor(quickPick));
    setConfirming(EmailBulkSendAction.ScheduleAll);
  };

  return (
    <div className="flex flex-col items-start gap-2 lg:items-end">
      <div className="inline-flex flex-nowrap">
        <Button
          color="sky"
          className="rounded-r-none"
          disabled={!!sendAllOffReason || isStarting}
          onClick={() => setConfirming(EmailBulkSendAction.SendAll)}
        >
          <PaperAirplaneIcon />
          {sendAllOffReason ? sendLabel : `${sendLabel} (${readyCount})`}
        </Button>
        <ScheduleSendMenu
          disabledReason={offReason ?? undefined}
          isSendNowDisabled={!!sendAllOffReason}
          sendNowDescription={isPastDailyLimit ? DAILY_BULK_LIMIT_REACHED : undefined}
          onSendNow={() => setConfirming(EmailBulkSendAction.SendAll)}
          onSchedule={(_sendAt, quickPick) => openSchedule(quickPick)}
          onPickDateAndTime={() => openSchedule()}
          moreChoices={
            isRest
              ? []
              : [
                  {
                    label: 'Move all to drafts',
                    icon: InboxArrowDownIcon,
                    onSelect: () => setConfirming(EmailBulkSendAction.MoveToDrafts)
                  }
                ]
          }
        />
      </div>
      {sendAllOffReason && (
        <span className="text-xs text-neutral-500 dark:text-neutral-400">{sendAllOffReason}</span>
      )}

      {confirming === EmailBulkSendAction.ScheduleAll && (
        <ScheduleSendDialog
          mode="schedule"
          provider={sendingMailbox?.provider}
          submitting={isStarting}
          error={error}
          initialQuickPick={scheduleStart.quickPick}
          initialSendAt={scheduleStart.sendAt}
          onSubmit={(sendAt) => start(EmailBulkSendAction.ScheduleAll, sendAt)}
          onClose={close}
          batchWording={{
            title: isRest ? 'Schedule the rest' : 'Schedule all',
            submitLabel: `Schedule ${emails}`,
            errorTitle: "We couldn't start this bulk send.",
            sendTimeLabel: 'Starts',
            sendNowLabel: sendLabel,
            footnote: [
              sendingFrom,
              SENDING_IS_FINAL,
              heldBackCount > 0 && heldBackLine(heldBackCount)
            ]
              .filter(Boolean)
              .join(' ')
          }}
        />
      )}

      <StartConfirm
        open={confirming === EmailBulkSendAction.SendAll}
        title={isRest ? `Send the ${emails} that didn't go out?` : `Send ${emails}?`}
        confirmLabel={`Send ${emails}`}
        isStarting={isStarting}
        error={error}
        onConfirm={() => start(EmailBulkSendAction.SendAll)}
        onClose={close}
      >
        <p>{sendingFrom}</p>
        <p>{SENDING_IS_FINAL}</p>
        {heldBackCount > 0 && <p>{heldBackLine(heldBackCount)}</p>}
      </StartConfirm>

      <StartConfirm
        open={confirming === EmailBulkSendAction.MoveToDrafts}
        title={`Move ${emails} to drafts?`}
        confirmLabel={`Move ${readyCount} to drafts`}
        isStarting={isStarting}
        error={error}
        onConfirm={() => start(EmailBulkSendAction.MoveToDrafts)}
        onClose={close}
      >
        <p>
          Cara saves each one as its own draft in the {draftsLocationName(sendingMailbox?.provider)}{' '}
          of {fromAddress}. Nothing is sent.
        </p>
        <p>
          Open and send them one at a time from {mailClientName(sendingMailbox?.provider)} or from
          Outgoing.
        </p>
        <p>
          Only Composed emails are moved.
          {heldBackCount > 0 && ` ${heldBackLine(heldBackCount)}`}
        </p>
      </StartConfirm>
    </div>
  );
};

const StartConfirm = ({
  open,
  title,
  confirmLabel,
  isStarting,
  error,
  onConfirm,
  onClose,
  children
}: React.PropsWithChildren<{
  open: boolean;
  title: string;
  confirmLabel: string;
  isStarting: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}>) => (
  <Dialog open={open} onClose={isStarting ? () => {} : onClose} size="md">
    <DialogTitle>{title}</DialogTitle>
    <DialogBody className="space-y-3 text-sm text-neutral-700 dark:text-neutral-300">
      {error && (
        <InlineNotice tone="error" title="We couldn't start this bulk send." description={error} />
      )}
      {children}
    </DialogBody>
    <DialogActions>
      <Button color="white" disabled={isStarting} onClick={onClose}>
        Cancel
      </Button>
      <Button color="sky" loading={isStarting} onClick={onConfirm}>
        {isStarting ? 'Starting…' : confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);
